import { useModal } from "@/app/shared/modal-views/use-modal";
import useApi from "@/hooks/useApi";
import PlacesRepository from "@/utilities/repositories/Places";
import { useRef, useState } from "react";
import toast from "react-hot-toast";
import {
  PiDownloadSimple,
  PiUploadSimple,
  PiXBold,
  PiInfoBold,
} from "react-icons/pi";
import { ActionIcon, Button, Title, Text } from "rizzui";
import {
  parseCSV,
  validateCSVData,
  ValidationError,
  CSVPlaceData,
} from "@/utils/csv-validation";
import { processCategoryForCSV } from "@/utils/category-mapping";
import ReviewCSVModal from "./review-csv-modal";

interface ImportCSVModalProps {
  refetchData?: () => void;
}

export default function ImportCSVModal({ refetchData }: ImportCSVModalProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { closeModal, isOpen } = useModal();
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>(
    []
  );
  const [isValidating, setIsValidating] = useState(false);
  const [showValidationReport, setShowValidationReport] = useState(false);
  const [reviewData, setReviewData] = useState<CSVPlaceData[]>([]);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const { loading: importLoading, request: importPlaces } = useApi({
    request: PlacesRepository.importPlaces,
  });

  const handleDownloadTemplate = () => {
    // Create CSV template with headers and instructions
    const headers = ["name", "category", "latitude", "longitude"];
    const sampleData = ["Sample Place", "Restaurant", "40.7128", "-74.0060"];

    const csvContent = [headers.join(","), sampleData.join(",")].join("\n");

    // Create and download the file
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "places_template.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success("Template downloaded successfully");
  };

  const handleSelectFile = () => {
    fileInputRef.current?.click();
  };

  const handleReviewConfirm = async () => {
    if (selectedFile) {
      await handleConfirmImport(selectedFile);
    }
  };

  const handleReviewCancel = () => {
    setShowReviewModal(false);
    setReviewData([]);
    setSelectedFile(null);
  };

  const handleConfirmImport = async (file: File) => {
    try {
      // Process the file again with fuzzy matching before import
      const fileText = await file.text();
      const parsedData = parseCSV(fileText);
      const processedData = processCSVWithFuzzyMatching(parsedData);

      const formData = new FormData();
      formData.append("file", file);
      await importPlaces(formData);
      toast.success(
        `CSV imported successfully! ${processedData.length} places added.`
      );
      refetchData?.();
      setShowReviewModal(false);
      if (isOpen) {
        closeModal();
      }
    } catch (error: unknown) {
      toast.error(
        error instanceof Error ? error.message : "Failed to import CSV"
      );
    }
  };

  // Process CSV data with fuzzy category matching
  const processCSVWithFuzzyMatching = (data: CSVPlaceData[]): CSVPlaceData[] => {
    return data.map(row => {
      // Process category with fuzzy matching
      const processedCategory = processCategoryForCSV(row.category || "");

      return {
        ...row,
        category: processedCategory || undefined // Set to undefined if no match found
      };
    });
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith(".csv")) {
      toast.error("Please select a CSV file");
      return;
    }

    setIsValidating(true);
    setValidationErrors([]);
    setShowValidationReport(false);

    try {
      // Read and validate CSV file
      const fileText = await file.text();
      const parsedData = parseCSV(fileText);

      // Process categories with fuzzy matching
      const processedData = processCSVWithFuzzyMatching(parsedData);
      const validationResult = validateCSVData(processedData);

      if (validationResult.isValid) {
        // If validation passes, show review modal with processed data
        setSelectedFile(file);
        setReviewData(validationResult.validData);
        setShowReviewModal(true);
      } else {
        // If validation fails, show errors
        setValidationErrors(validationResult.errors);
        setReviewData(validationResult.validData);
        setShowValidationReport(true);
        setShowReviewModal(true);
        setSelectedFile(file);
        toast.error(
          `Validation failed: ${validationResult.errors.length} errors found`
        );
      }
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to validate CSV";
      if (errorMessage.includes("Missing required headers")) {
        toast.error("CSV format error: Required headers are missing");
      } else if (errorMessage.includes("Must contain at least")) {
        toast.error("CSV must contain at least one data row");
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setIsValidating(false);
      // Reset the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  return (
    <div className="p-6 @container">
      <div className="flex items-center justify-between mb-6">
        <Title as="h5" className="text-xl md:text-2xl">
          Import Places from CSV
        </Title>
        {isOpen && (
          <ActionIcon size="sm" variant="text" onClick={closeModal}>
            <PiXBold className="h-auto w-5" />
          </ActionIcon>
        )}
      </div>

      <div className="space-y-6">
        {/* Instructions */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <Text className="font-medium mb-2">Instructions:</Text>
          <ul className="text-sm text-gray-600 space-y-1">
            <li>1. Download the CSV template below</li>
            <li>2. Fill in your places data following the template format</li>
            <li>3. Upload your completed CSV file</li>
            <li>4. Required fields: name, latitude, longitude</li>
            <li>5. Optional fields: category</li>
            <li>6. File will be validated before import</li>
          </ul>
        </div>

        {/* Template Download */}
        <div className="border border-gray-200 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <Text className="font-medium">CSV Template</Text>
              <Text className="text-sm text-gray-600">
                Download the template with sample data and correct headers
              </Text>
            </div>
            <Button
              onClick={handleDownloadTemplate}
              variant="outline"
              size="sm"
            >
              <PiDownloadSimple className="mr-2 h-4 w-4" />
              <span className="text-xs w-max">Download Template</span>
            </Button>
          </div>
        </div>

        {/* File Upload */}
        <div className="border border-dashed border-gray-300 p-6 rounded-lg text-center">
          <PiUploadSimple className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <Text className="font-medium mb-2">Upload your CSV file</Text>
          <Text className="text-sm text-gray-600 mb-4">
            Select a CSV file with your places data
          </Text>
          <Button
            onClick={handleSelectFile}
            isLoading={importLoading || isValidating}
            className="w-auto"
          >
            <PiUploadSimple className="mr-2 h-4 w-4" />
            {isValidating ? "Validating..." : "Select CSV File"}
          </Button>
        </div>

        {/* Validation Errors */}
        {showValidationReport && validationErrors.length > 0 && (
          <div className="border border-red-200 bg-red-50 p-4 rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <Text className="font-medium text-red-800">
                Validation Errors ({validationErrors.length})
              </Text>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowValidationReport(false)}
              >
                Hide
              </Button>
            </div>
            <div className="max-h-48 overflow-y-auto text-sm">
              {Object.entries(
                validationErrors.reduce(
                  (acc, error) => {
                    if (!acc[error.field]) acc[error.field] = [];
                    acc[error.field].push(error);
                    return acc;
                  },
                  {} as Record<string, ValidationError[]>
                )
              ).map(([field, errors]) => (
                <div key={field} className="mb-3">
                  <Text className="font-medium text-red-700 capitalize">
                    {field} ({errors.length}):
                  </Text>
                  {errors.map((error, index) => (
                    <div key={index} className="text-red-600 text-xs ml-2">
                      Row {error.row}: {error.message}
                      {error.value && (
                        <span className="ml-2 text-red-500">
                          (Value: "{error.value}")
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          onChange={handleFileChange}
          style={{ display: "none" }}
        />

        {/* Action buttons */}
        {!showReviewModal && (
          <div className="flex items-center justify-end gap-4 pt-4 border-t">
            <Button variant="outline" onClick={closeModal} className="w-auto">
              Cancel
            </Button>
          </div>
        )}
      </div>

      {/* Review Modal */}
      {showReviewModal && (
        <ReviewCSVModal
          csvData={reviewData}
          onConfirm={handleReviewConfirm}
          onCancel={handleReviewCancel}
          isImporting={importLoading}
        />
      )}
    </div>
  );
}
